/**
 * Self-test suite for AccountIdentifierRotation.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/AccountIdentifierRotationSelfTest.swift`.
 *
 * Coverage:
 *
 *   A. Notice construction & verification (pure crypto, no I/O):
 *      1.  Sign-then-verify round-trip with correct public key
 *      2.  Verification fails when signed bytes are tampered
 *      3.  Verification fails with wrong public key
 *      4.  Notice from the future rejected
 *      5.  Expired notice rejected
 *      6.  oldUserId == newUserId rejected at construction
 *      7.  Empty userId rejected at construction
 *      8.  Unsupported notice version rejected at verification
 *      9.  Canonical signed-bytes encoding is deterministic
 *
 *   B. Local-state migration (in-memory mock stores):
 *     10.  applyPeerAccountRotation migrates sessions (multiple devices, deviceId preserved)
 *     11.  applyPeerAccountRotation moves trusted-identity pin
 *     12.  applyPeerAccountRotation moves IdentityVerification record (state preserved)
 *     13.  applyPeerAccountRotation moves KeyAudit history
 *     14.  applyPeerAccountRotation rejected when oldUserId has no trusted pin
 *     15.  applyPeerAccountRotation rejects forged signatures (no migration on failure)
 *     16.  applyPeerAccountRotation is idempotent: second apply returns replay=true
 *     17.  Sessions/pins for other users are not touched
 *
 *   C. Owner-side rotation:
 *     18.  rotateOwnAccountIdentifier produces a verifiable notice
 *     19.  rotateOwnAccountIdentifier appends kind="self" log entry
 *
 *   D. Additional edge cases:
 *     20.  Old-userId reference cleanup: after migration, querying by oldUserId returns null
 *     21.  Owner-side: session record bytes preserved unchanged after migration
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  AccountIdentifierRotationError,
  CURRENT_NOTICE_VERSION,
  DEFAULT_MAX_AGE_MS,
  DEFAULT_MAX_FUTURE_SKEW_MS,
  InMemoryRotationLog,
  signedBytes,
  signNotice,
  verifyNotice,
  rotateOwnAccountIdentifier,
  applyPeerAccountRotation,
  migrateLocalState,
} from "./AccountIdentifierRotation";
import type {
  AccountRotationNotice,
  AccountSessionMigrationStore,
  IdentityVerificationMigrationStore,
  IdentityVerificationRecord,
  KeyAuditMigrationStore,
  KeyAuditEntry,
  MigrationResult,
} from "./AccountIdentifierRotation";

// ---------------------------------------------------------------------------
// In-memory mock stores
// ---------------------------------------------------------------------------

/**
 * In-memory `AccountSessionMigrationStore`. Keeps the production SignalStore
 * (and its IndexedDB) untouched. Behaviour is byte-equivalent to the
 * production store for the surface `AccountIdentifierRotation` depends on.
 *
 * Session key format: `"<userId>.<deviceId>"` — same as SignalProtocolAddress.
 * The `sessionAddresses` helper returns every key whose prefix matches userId.
 */
class InMemorySessionStore implements AccountSessionMigrationStore {
  private sessions = new Map<string, string>();      // key = "<userId>.<dev>"
  private trustedIds = new Map<string, string>();    // key = userId, value = B64 pubkey

  async sessionAddresses(userId: string): Promise<string[]> {
    const prefix = `${userId}.`;
    return Array.from(this.sessions.keys()).filter((k) => k.startsWith(prefix));
  }

  async loadSession(addr: string): Promise<string | undefined> {
    return this.sessions.get(addr);
  }

  async storeSession(addr: string, record: string): Promise<void> {
    this.sessions.set(addr, record);
  }

  async removeSession(addr: string): Promise<void> {
    this.sessions.delete(addr);
  }

  async getTrustedIdentity(userId: string): Promise<string | undefined> {
    return this.trustedIds.get(userId);
  }

  async storeTrustedIdentity(userId: string, publicKeyB64: string): Promise<void> {
    this.trustedIds.set(userId, publicKeyB64);
  }

  async removeTrustedIdentity(userId: string): Promise<void> {
    this.trustedIds.delete(userId);
  }
}

/**
 * In-memory `IdentityVerificationMigrationStore`.
 */
class InMemoryVerificationStore implements IdentityVerificationMigrationStore {
  private records = new Map<string, IdentityVerificationRecord>();

  async getRecord(userId: string): Promise<IdentityVerificationRecord | undefined> {
    return this.records.get(userId);
  }

  async setRecord(userId: string, record: IdentityVerificationRecord): Promise<void> {
    this.records.set(userId, record);
  }

  async migrate(oldUserId: string, newUserId: string): Promise<boolean> {
    const rec = this.records.get(oldUserId);
    if (rec === undefined) return false;
    this.records.set(newUserId, { ...rec, userId: newUserId });
    this.records.delete(oldUserId);
    return true;
  }
}

/**
 * In-memory `KeyAuditMigrationStore`.
 */
class InMemoryKeyAuditStore implements KeyAuditMigrationStore {
  private entries: KeyAuditEntry[] = [];

  async historyFor(userId: string): Promise<KeyAuditEntry[]> {
    return this.entries.filter((e) => e.userId === userId);
  }

  async appendEntry(entry: KeyAuditEntry): Promise<void> {
    this.entries.push(entry);
  }

  async migrate(oldUserId: string, newUserId: string): Promise<boolean> {
    const toMove = this.entries.filter((e) => e.userId === oldUserId);
    if (toMove.length === 0) return false;
    this.entries = this.entries.map((e) =>
      e.userId === oldUserId ? { ...e, userId: newUserId } : e,
    );
    return true;
  }
}

// ---------------------------------------------------------------------------
// Ed25519 key generation helper (SubtleCrypto)
// ---------------------------------------------------------------------------

interface KeyPair {
  publicKeyBytes: Uint8Array;
  privateKeyBytes: Uint8Array;
}

/**
 * Generate a fresh Ed25519 key pair using SubtleCrypto.
 * Export the raw key bytes so we can pass them to `signNotice` / `verifyNotice`.
 *
 * Chrome refuses `exportKey("raw", privateKey)` for Ed25519; the private key
 * must be exported as PKCS#8 and the 32-byte seed extracted from the tail of
 * the blob (the last 32 bytes of the constant-prefix structure).
 */
async function generateKeyPair(): Promise<KeyPair> {
  const pair = await crypto.subtle.generateKey(
    { name: "Ed25519" },
    true,
    ["sign", "verify"],
  ) as CryptoKeyPair;

  const pubBuffer = await crypto.subtle.exportKey("raw", pair.publicKey);
  const privPkcs8 = new Uint8Array(
    await crypto.subtle.exportKey("pkcs8", pair.privateKey),
  );
  // PKCS#8 for Ed25519: 16-byte header + 32-byte seed (total 48 bytes).
  const privateKeyBytes = privPkcs8.slice(privPkcs8.length - 32);
  return {
    publicKeyBytes: new Uint8Array(pubBuffer),
    privateKeyBytes,
  };
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

// ---------------------------------------------------------------------------
// Environment factory
// ---------------------------------------------------------------------------

interface TestEnv {
  store: InMemorySessionStore;
  verificationStore: InMemoryVerificationStore;
  keyAuditStore: InMemoryKeyAuditStore;
  rotationLog: InMemoryRotationLog;
  kp: KeyPair;
}

async function makeEnv(): Promise<TestEnv> {
  return {
    store: new InMemorySessionStore(),
    verificationStore: new InMemoryVerificationStore(),
    keyAuditStore: new InMemoryKeyAuditStore(),
    rotationLog: new InMemoryRotationLog(),
    kp: await generateKeyPair(),
  };
}

// ---------------------------------------------------------------------------
// A. Notice construction & verification
// ---------------------------------------------------------------------------

async function tcRoundTrip(): Promise<TestCase> {
  const name = "sign + verify round-trip with correct public key";
  try {
    const kp = await generateKeyPair();
    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: "alice@old",
      newUserId: "alice@new",
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, kp.privateKeyBytes);
    await verifyNotice(notice, kp.publicKeyBytes);
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function tcTamperedSignedBytes(): Promise<TestCase> {
  const name = "tampered newUserId is rejected";
  try {
    const kp = await generateKeyPair();
    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: "alice@old",
      newUserId: "alice@new",
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, kp.privateKeyBytes);
    // Tamper: different newUserId, same signature.
    const tampered: AccountRotationNotice = {
      ...notice,
      newUserId: "attacker@takeover",
    };
    try {
      await verifyNotice(tampered, kp.publicKeyBytes);
      return fail(name, "verifyNotice did not throw on tampered notice");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "signatureVerificationFailed"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcWrongPublicKey(): Promise<TestCase> {
  const name = "verification fails with wrong public key";
  try {
    const signer = await generateKeyPair();
    const other = await generateKeyPair();
    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: "alice@old",
      newUserId: "alice@new",
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, signer.privateKeyBytes);
    try {
      await verifyNotice(notice, other.publicKeyBytes);
      return fail(name, "verifyNotice did not throw with wrong key");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "signatureVerificationFailed"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcNoticeFromFuture(): Promise<TestCase> {
  const name = "notice from the future is rejected";
  try {
    const kp = await generateKeyPair();
    const nowMs = Date.now();
    const futureMs = nowMs + 60 * 60 * 1000; // 1 hour ahead, beyond 5-min skew
    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: "alice@old",
      newUserId: "alice@new",
      issuedAtMillis: futureMs,
    };
    const notice = await signNotice(unsigned, kp.privateKeyBytes);
    try {
      await verifyNotice(notice, kp.publicKeyBytes, nowMs);
      return fail(name, "verifyNotice did not throw for future notice");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "noticeFromFuture"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcExpiredNotice(): Promise<TestCase> {
  const name = "expired notice is rejected";
  try {
    const kp = await generateKeyPair();
    const nowMs = Date.now();
    const pastMs = nowMs - 60 * 24 * 60 * 60 * 1000; // 60 days ago
    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: "alice@old",
      newUserId: "alice@new",
      issuedAtMillis: pastMs,
    };
    const notice = await signNotice(unsigned, kp.privateKeyBytes);
    try {
      await verifyNotice(notice, kp.publicKeyBytes, nowMs);
      return fail(name, "verifyNotice did not throw for expired notice");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "noticeExpired"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcSameUserIdRejected(): Promise<TestCase> {
  const name = "oldUserId == newUserId rejected at construction";
  try {
    const kp = await generateKeyPair();
    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: "alice",
      newUserId: "alice",
      issuedAtMillis: Date.now(),
    };
    // signNotice itself does not validate — the check is in rotateOwn/applyPeer.
    // For pure-construction validation, use rotateOwnAccountIdentifier.
    try {
      await rotateOwnAccountIdentifier(
        "alice",
        "alice",
        kp.privateKeyBytes,
        kp.publicKeyBytes,
        {
          store: new InMemorySessionStore(),
          verificationStore: new InMemoryVerificationStore(),
          keyAuditStore: new InMemoryKeyAuditStore(),
          rotationLog: new InMemoryRotationLog(),
        },
      );
      return fail(name, "did not throw");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "sameUserId"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcEmptyUserIdRejected(): Promise<TestCase> {
  const name = "empty userId rejected at construction";
  try {
    const kp = await generateKeyPair();
    try {
      await rotateOwnAccountIdentifier(
        "",
        "alice@new",
        kp.privateKeyBytes,
        kp.publicKeyBytes,
        {
          store: new InMemorySessionStore(),
          verificationStore: new InMemoryVerificationStore(),
          keyAuditStore: new InMemoryKeyAuditStore(),
          rotationLog: new InMemoryRotationLog(),
        },
      );
      return fail(name, "did not throw");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "emptyUserId"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcUnsupportedVersionRejected(): Promise<TestCase> {
  const name = "unsupported notice version rejected at verification";
  try {
    const kp = await generateKeyPair();
    const bytes = signedBytes(999, "alice@old", "alice@new", Date.now());
    // Chrome rejects `importKey("raw", ...)` for Ed25519 private keys; wrap
    // the 32-byte seed in the constant PKCS#8 header (RFC 8410 §7) — same
    // prefix used by `wrapEd25519Pkcs8` in AccountIdentifierRotation.ts.
    const pkcs8 = new Uint8Array(16 + kp.privateKeyBytes.length);
    pkcs8.set(
      [
        0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
        0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
      ],
      0,
    );
    pkcs8.set(kp.privateKeyBytes, 16);
    const privKey = await crypto.subtle.importKey(
      "pkcs8",
      pkcs8,
      { name: "Ed25519" },
      false,
      ["sign"],
    );
    const sigBuffer = await crypto.subtle.sign("Ed25519", privKey, bytes);
    let binary = "";
    new Uint8Array(sigBuffer).forEach((b) => (binary += String.fromCharCode(b)));
    const notice: AccountRotationNotice = {
      version: 999,
      oldUserId: "alice@old",
      newUserId: "alice@new",
      issuedAtMillis: Date.now(),
      signature: btoa(binary),
    };
    try {
      await verifyNotice(notice, kp.publicKeyBytes);
      return fail(name, "verifyNotice did not throw for unsupported version");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "unsupportedNoticeVersion"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcCanonicalBytesDeterministic(): Promise<TestCase> {
  const name = "canonical signed-bytes encoding is deterministic";
  const a = signedBytes(1, "alice@old", "alice@new", 1_700_000_000_000);
  const b = signedBytes(1, "alice@old", "alice@new", 1_700_000_000_000);
  const c = signedBytes(1, "alice@old", "alice@new", 1_700_000_000_001);
  const equal = (x: Uint8Array, y: Uint8Array) =>
    x.length === y.length && x.every((v, i) => v === y[i]);
  const ok = equal(a, b) && !equal(a, c);
  return ok ? pass(name) : fail(name, `a==b: ${equal(a, b)}, a==c: ${equal(a, c)}`);
}

// ---------------------------------------------------------------------------
// B. Local-state migration
// ---------------------------------------------------------------------------

async function tcMigratesSessionsMultiDevice(): Promise<TestCase> {
  const name = "applyPeerAccountRotation migrates all sessions, deviceIds preserved";
  try {
    const env = await makeEnv();
    const oldId = "alice@old";
    const newId = "alice@new";

    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));
    // Seed 3 sessions.
    for (const dev of [1, 2, 3]) {
      const record = btoa(String.fromCharCode(...new Uint8Array(32).fill(0x10 + dev)));
      await env.store.storeSession(`${oldId}.${dev}`, record);
    }

    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: oldId,
      newUserId: newId,
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    const result = await applyPeerAccountRotation(notice, env, Date.now());

    if (result.sessionsMigrated !== 3) {
      return fail(name, `expected 3 sessions migrated, got ${result.sessionsMigrated}`);
    }
    if (result.replay) {
      return fail(name, "unexpected replay flag");
    }
    // Old addresses gone.
    const oldAddrs = await env.store.sessionAddresses(oldId);
    if (oldAddrs.length !== 0) {
      return fail(name, `old addresses still present: ${oldAddrs.join(", ")}`);
    }
    // New addresses present with original record bytes.
    for (const dev of [1, 2, 3]) {
      const rec = await env.store.loadSession(`${newId}.${dev}`);
      const expected = btoa(String.fromCharCode(...new Uint8Array(32).fill(0x10 + dev)));
      if (rec !== expected) {
        return fail(name, `session record mismatch at deviceId=${dev}`);
      }
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function tcMovesTrustedIdentity(): Promise<TestCase> {
  const name = "trusted-identity pin moves to new userId";
  try {
    const env = await makeEnv();
    const oldId = "alice@old";
    const newId = "alice@new";
    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));

    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: oldId,
      newUserId: newId,
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    const result = await applyPeerAccountRotation(notice, env, Date.now());

    if (!result.trustedIdentityMoved) {
      return fail(name, `trustedIdentityMoved=false, result: ${JSON.stringify(result)}`);
    }
    const oldPin = await env.store.getTrustedIdentity(oldId);
    if (oldPin !== undefined) {
      return fail(name, "old pin still present after migration");
    }
    const newPin = await env.store.getTrustedIdentity(newId);
    if (newPin !== uint8ToBase64(env.kp.publicKeyBytes)) {
      return fail(name, "new pin missing or wrong bytes");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function tcMovesVerificationRecord(): Promise<TestCase> {
  const name = "verification record moves and preserves state";
  try {
    const env = await makeEnv();
    const oldId = "alice@old";
    const newId = "alice@new";
    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));

    // Seed a "verified" record.
    await env.verificationStore.setRecord(oldId, {
      userId: oldId,
      identityPubKeyB64: uint8ToBase64(env.kp.publicKeyBytes),
      state: "verified",
      verifiedAtMs: Date.now() - 1000,
    });

    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: oldId,
      newUserId: newId,
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    await applyPeerAccountRotation(notice, env, Date.now());

    const oldRec = await env.verificationStore.getRecord(oldId);
    if (oldRec !== undefined) {
      return fail(name, "old record still present after migration");
    }
    const newRec = await env.verificationStore.getRecord(newId);
    if (!newRec || newRec.state !== "verified") {
      return fail(name, `new record missing or state changed: ${JSON.stringify(newRec)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function tcMovesKeyAuditHistory(): Promise<TestCase> {
  const name = "audit log moves and preserves history";
  try {
    const env = await makeEnv();
    const oldId = "alice@old";
    const newId = "alice@new";
    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));

    // Seed 2 audit entries.
    await env.keyAuditStore.appendEntry({
      userId: oldId,
      identityPubKeyB64: uint8ToBase64(env.kp.publicKeyBytes),
      observedAtMs: 1_000,
      source: "preKeyBundle",
    });
    await env.keyAuditStore.appendEntry({
      userId: oldId,
      identityPubKeyB64: uint8ToBase64(env.kp.publicKeyBytes),
      observedAtMs: 2_000,
      source: "envelope",
    });

    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: oldId,
      newUserId: newId,
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    await applyPeerAccountRotation(notice, env, Date.now());

    const oldHistory = await env.keyAuditStore.historyFor(oldId);
    if (oldHistory.length !== 0) {
      return fail(name, `old log still has ${oldHistory.length} entries`);
    }
    const newHistory = await env.keyAuditStore.historyFor(newId);
    if (newHistory.length !== 2) {
      return fail(name, `expected 2 entries under new userId, got ${newHistory.length}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function tcRejectedWithoutTrustedPin(): Promise<TestCase> {
  const name = "applyPeerAccountRotation rejected when no trusted pin for oldUserId";
  try {
    const env = await makeEnv();
    // Deliberately no trusted pin.
    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: "ghost@old",
      newUserId: "ghost@new",
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    try {
      await applyPeerAccountRotation(notice, env, Date.now());
      return fail(name, "did not throw");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "missingTrustedIdentityForOldUserId"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcRejectsForgedSignature(): Promise<TestCase> {
  const name = "applyPeerAccountRotation rejects notice signed by wrong key";
  try {
    const env = await makeEnv();
    const attacker = await generateKeyPair();
    const oldId = "alice@old";

    // We trust the real peer's key.
    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));

    // Attacker signs with their own key.
    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: oldId,
      newUserId: "alice@hijacked",
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, attacker.privateKeyBytes);
    try {
      await applyPeerAccountRotation(notice, env, Date.now());
      return fail(name, "did not throw on forged signature");
    } catch (e) {
      if (
        e instanceof AccountIdentifierRotationError &&
        e.kind === "signatureVerificationFailed"
      ) {
        // Also assert no migration happened.
        const hijackedPin = await env.store.getTrustedIdentity("alice@hijacked");
        if (hijackedPin !== undefined) {
          return fail(name, "migration ran despite signature rejection");
        }
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `setup: ${String(e)}`);
  }
}

async function tcIdempotentReplay(): Promise<TestCase> {
  const name = "applyPeerAccountRotation is idempotent: second apply returns replay=true";
  try {
    const env = await makeEnv();
    const oldId = "alice@old";
    const newId = "alice@new";
    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));
    await env.store.storeSession(`${oldId}.1`, btoa(String.fromCharCode(...new Uint8Array(32).fill(0x42))));

    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: oldId,
      newUserId: newId,
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    const first = await applyPeerAccountRotation(notice, env, Date.now());

    // To exercise replay, restore the trusted pin under oldId (simulating
    // server re-delivering the notice after the client already migrated).
    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));
    const second = await applyPeerAccountRotation(notice, env, Date.now());

    if (first.replay !== false) {
      return fail(name, `first apply should not be replay, got replay=${first.replay}`);
    }
    if (first.sessionsMigrated !== 1) {
      return fail(name, `first apply: expected 1 session migrated, got ${first.sessionsMigrated}`);
    }
    if (second.replay !== true) {
      return fail(name, `second apply should be replay, got replay=${second.replay}`);
    }
    if (second.sessionsMigrated !== 0) {
      return fail(name, `second apply: expected 0 sessions migrated, got ${second.sessionsMigrated}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function tcDoesNotTouchOtherUsers(): Promise<TestCase> {
  const name = "rotation does not touch other users' sessions or pins";
  try {
    const env = await makeEnv();
    const bobKp = await generateKeyPair();
    const aliceOld = "alice@old";
    const aliceNew = "alice@new";

    await env.store.storeTrustedIdentity(aliceOld, uint8ToBase64(env.kp.publicKeyBytes));
    await env.store.storeTrustedIdentity("bob", uint8ToBase64(bobKp.publicKeyBytes));
    await env.store.storeSession(`${aliceOld}.1`, btoa(String.fromCharCode(...new Uint8Array(32).fill(0xAA))));
    await env.store.storeSession("bob.1", btoa(String.fromCharCode(...new Uint8Array(32).fill(0xBB))));

    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: aliceOld,
      newUserId: aliceNew,
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    await applyPeerAccountRotation(notice, env, Date.now());

    const bobPin = await env.store.getTrustedIdentity("bob");
    if (bobPin !== uint8ToBase64(bobKp.publicKeyBytes)) {
      return fail(name, "bob's trusted pin was lost or changed");
    }
    const bobSession = await env.store.loadSession("bob.1");
    if (bobSession !== btoa(String.fromCharCode(...new Uint8Array(32).fill(0xBB)))) {
      return fail(name, "bob's session was lost or corrupted");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// C. Owner-side rotation
// ---------------------------------------------------------------------------

async function tcSelfRotationProducesVerifiableNotice(): Promise<TestCase> {
  const name = "rotateOwnAccountIdentifier produces a verifiable notice";
  try {
    const env = await makeEnv();
    const { notice } = await rotateOwnAccountIdentifier(
      "me@old",
      "me@new",
      env.kp.privateKeyBytes,
      env.kp.publicKeyBytes,
      env,
    );
    // The notice must verify against our public key.
    await verifyNotice(notice, env.kp.publicKeyBytes);
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function tcSelfRotationLoggedWithSelfKind(): Promise<TestCase> {
  const name = "rotateOwnAccountIdentifier appends kind=self log entry";
  try {
    const env = await makeEnv();
    await rotateOwnAccountIdentifier(
      "me@old",
      "me@new",
      env.kp.privateKeyBytes,
      env.kp.publicKeyBytes,
      env,
    );
    const entries = env.rotationLog.entries();
    if (entries.length !== 1) {
      return fail(name, `expected 1 log entry, got ${entries.length}`);
    }
    const entry = entries[0];
    if (entry.kind !== "self") {
      return fail(name, `expected kind=self, got ${entry.kind}`);
    }
    if (entry.oldUserId !== "me@old" || entry.newUserId !== "me@new") {
      return fail(name, `unexpected userIds: ${entry.oldUserId} → ${entry.newUserId}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// D. Additional edge cases
// ---------------------------------------------------------------------------

async function tcOldUserIdCleanup(): Promise<TestCase> {
  const name = "after migration querying stores by oldUserId returns null/undefined";
  try {
    const env = await makeEnv();
    const oldId = "alice@old";
    const newId = "alice@new";
    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));
    await env.store.storeSession(`${oldId}.1`, "some-record");
    await env.verificationStore.setRecord(oldId, {
      userId: oldId,
      identityPubKeyB64: uint8ToBase64(env.kp.publicKeyBytes),
      state: "tofu",
    });

    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: oldId,
      newUserId: newId,
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    await applyPeerAccountRotation(notice, env, Date.now());

    const oldSessions = await env.store.sessionAddresses(oldId);
    if (oldSessions.length !== 0) {
      return fail(name, `sessions still indexed by oldUserId: ${oldSessions}`);
    }
    const oldPin = await env.store.getTrustedIdentity(oldId);
    if (oldPin !== undefined) {
      return fail(name, "trusted pin still under oldUserId");
    }
    const oldVerif = await env.verificationStore.getRecord(oldId);
    if (oldVerif !== undefined) {
      return fail(name, "verification record still under oldUserId");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function tcSessionCryptoStatePreserved(): Promise<TestCase> {
  const name = "session record bytes unchanged after migration (crypto state preserved)";
  try {
    const env = await makeEnv();
    const oldId = "alice@old";
    const newId = "alice@new";
    // Simulate a ratchet record blob (arbitrary bytes — migration must not touch them).
    const ratchetRecord = btoa(
      String.fromCharCode(...Array.from({ length: 64 }, (_, i) => i)),
    );
    await env.store.storeTrustedIdentity(oldId, uint8ToBase64(env.kp.publicKeyBytes));
    await env.store.storeSession(`${oldId}.1`, ratchetRecord);

    const unsigned: Omit<AccountRotationNotice, "signature"> = {
      version: CURRENT_NOTICE_VERSION,
      oldUserId: oldId,
      newUserId: newId,
      issuedAtMillis: Date.now(),
    };
    const notice = await signNotice(unsigned, env.kp.privateKeyBytes);
    await applyPeerAccountRotation(notice, env, Date.now());

    const migratedRecord = await env.store.loadSession(`${newId}.1`);
    if (migratedRecord !== ratchetRecord) {
      return fail(
        name,
        `record bytes changed: expected ${ratchetRecord.slice(0, 20)}…, got ${(migratedRecord ?? "").slice(0, 20)}…`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Account identifier rotation (PNI-equivalent)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Notice construction & verification
        tcRoundTrip(),
        tcTamperedSignedBytes(),
        tcWrongPublicKey(),
        tcNoticeFromFuture(),
        tcExpiredNotice(),
        tcSameUserIdRejected(),
        tcEmptyUserIdRejected(),
        tcUnsupportedVersionRejected(),
        tcCanonicalBytesDeterministic(),
        // B. Local-state migration
        tcMigratesSessionsMultiDevice(),
        tcMovesTrustedIdentity(),
        tcMovesVerificationRecord(),
        tcMovesKeyAuditHistory(),
        tcRejectedWithoutTrustedPin(),
        tcRejectsForgedSignature(),
        tcIdempotentReplay(),
        tcDoesNotTouchOtherUsers(),
        // C. Owner-side rotation
        tcSelfRotationProducesVerifiableNotice(),
        tcSelfRotationLoggedWithSelfKind(),
        // D. Additional edge cases
        tcOldUserIdCleanup(),
        tcSessionCryptoStatePreserved(),
      ]),
    ),
});
